import { supabase, getProdSupabaseClient } from './client';
import { isSystemError, logSystemError } from '@/lib/utils/errorTranslator';
import { getTenantIdFromSession } from './tenantScoping';
import { getProfilePhotoUrl, extractStoragePathFromUrl } from './profile-helpers';

export interface UserProfile {
  id: string;
  user_id: string;
  first_name?: string;
  last_name?: string;
  profile_photo_url?: string;
  phone_number?: string;
  job_title?: string;
  employee_id?: string;
  preferred_language: string;
  timezone: string;
  hire_date?: string | null;
  work_phone?: string | null;
  work_email?: string | null;
  department?: string;
  manager_id?: string;
  certifications?: string[];
  specializations?: string[];
  languages?: string[];
  skills?: string[];
  years_experience?: number | null;
  shift_preference?: string | null;
  personal_email?: string | null;
  mobile_phone?: string | null;
  home_phone?: string | null;
  emergency_contact_name?: string | null;
  emergency_contact_phone?: string | null;
  emergency_contact_relationship?: string | null;
  profile_completion_percentage: number;
  onboarding_step: string;
  onboarding_completed: boolean;
  onboarding_completed_at?: string;
  created_at: string;
  updated_at: string;
}

export interface NotificationPreferences {
  id: string;
  user_id: string;
  email_notifications: boolean;
  email_recall_alerts: boolean;
  email_campaign_updates: boolean;
  email_system_notifications: boolean;
  email_weekly_reports: boolean;
  sms_notifications: boolean;
  sms_urgent_alerts: boolean;
  sms_phone_number?: string;
  in_app_notifications: boolean;
  in_app_sound: boolean;
  in_app_badge: boolean;
  notification_frequency: string;
  quiet_hours_start?: string;
  quiet_hours_end?: string;
  quiet_hours_enabled: boolean;
  created_at: string;
  updated_at: string;
}

export interface ApiKey {
  id: string;
  user_id: string;
  key_name: string;
  key_value: string;
  key_prefix: string;
  permissions: string[];
  allowed_ips?: string[];
  allowed_origins?: string[];
  is_active: boolean;
  last_used_at?: string;
  usage_count: number;
  expires_at?: string;
  created_at: string;
  updated_at: string;
}

export interface UserOnboardingProgress {
  id: string;
  user_id: string;
  current_step: string;
  completed_steps: string[];
  skipped_steps: string[];
  profile_setup_completed_at?: string;
  permissions_reviewed_at?: string;
  training_completed_at?: string;
  manager_approval_requested_at?: string;
  manager_approval_received_at?: string;
  activation_completed_at?: string;
  training_modules_completed: string[];
  training_quiz_scores: Record<string, number>;
  training_certificates: string[];
  manager_id?: string;
  manager_notes?: string;
  approval_status: string;
  created_at: string;
  updated_at: string;
}

export interface UserPreferences {
  id: string;
  user_id: string;
  theme: string;
  language: string;
  timezone: string;
  dashboard_layout: Record<string, any>;
  default_views: Record<string, any>;
  widget_preferences: Record<string, any>;
  auto_assign_campaigns: boolean;
  preferred_work_hours: Record<string, any>;
  notification_sound: boolean;
  created_at: string;
  updated_at: string;
}

export const profileAPI = {
  // User Profile Management
  async getUserProfile(userId: string): Promise<UserProfile | null> {
    try {
      // Get tenant_id for the user
      const tenantId = await getTenantIdFromSession(supabase);
      if (!tenantId) {
        console.warn(
          'No tenant_id available for getting user profile, falling back to auth metadata'
        );
        return await this.getUserProfileFromAuth(userId);
      }

      // Use prod schema for user_profiles
      const prodClient = getProdSupabaseClient();
      const { data, error } = await prodClient
        .from('user_profiles')
        .select('*')
        .eq('user_id', userId)
        .eq('tenant_id', tenantId)
        .maybeSingle();

      if (error) {
        logSystemError(error, 'fetching user profile');
        // Fallback to auth metadata on error
        return await this.getUserProfileFromAuth(userId);
      }

      if (!data) {
        // No profile in prod.user_profiles, try auth metadata as fallback
        return await this.getUserProfileFromAuth(userId);
      }

      // Convert profile_photo_path to full URL
      const profilePhotoUrl = getProfilePhotoUrl(data.profile_photo_path);

      // Get auth user for name information
      const { data: authData } = await supabase.auth.getUser();
      const authUser = authData?.user;

      // Parse full_name from auth metadata, or use first_name/last_name if available
      const fullName = authUser?.user_metadata?.full_name || '';
      const nameParts = fullName.split(' ');
      const firstName = nameParts[0] || '';
      const lastName = nameParts.slice(1).join(' ') || '';

      // Map prod.user_profiles data to UserProfile interface
      return {
        id: data.id,
        user_id: data.user_id,
        first_name: firstName,
        last_name: lastName,
        profile_photo_url: profilePhotoUrl,
        phone_number: data.phone_number || '',
        job_title: data.job_title || '',
        employee_id: '',
        preferred_language: 'en',
        timezone: 'UTC',
        hire_date: null,
        work_phone: null,
        work_email: null,
        department: data.department || '',
        manager_id: null,
        certifications: [],
        specializations: [],
        languages: [],
        skills: [],
        years_experience: null,
        shift_preference: null,
        personal_email: null,
        mobile_phone: data.phone_number || null,
        home_phone: null,
        emergency_contact_name: null,
        emergency_contact_phone: null,
        emergency_contact_relationship: null,
        profile_completion_percentage: 0,
        onboarding_step: 'profile_setup',
        onboarding_completed: false,
        onboarding_completed_at: '',
        created_at: data.created_at,
        updated_at: data.updated_at
      };
    } catch (error) {
      if (isSystemError(error)) {
        logSystemError(error, 'getting user profile');
      }
      // Fallback to auth metadata
      return await this.getUserProfileFromAuth(userId);
    }
  },

  // Helper method to get profile from auth metadata (fallback)
  async getUserProfileFromAuth(userId: string): Promise<UserProfile | null> {
    try {
      const { data: authData } = await supabase.auth.getUser();
      if (!authData?.user || authData.user.id !== userId) {
        return null;
      }

      const metadata = authData.user.user_metadata || {};

      return {
        id: authData.user.id,
        user_id: authData.user.id,
        first_name: metadata.full_name?.split(' ')[0] || '',
        last_name: metadata.full_name?.split(' ').slice(1).join(' ') || '',
        profile_photo_url: metadata.profile_photo_url || undefined,
        phone_number: metadata.mobile_phone || metadata.phone_number || '',
        job_title: metadata.job_title || '',
        employee_id: '',
        preferred_language: 'en',
        timezone: 'UTC',
        hire_date: null,
        work_phone: null,
        work_email: null,
        department: metadata.department || '',
        manager_id: null,
        certifications: [],
        specializations: [],
        languages: [],
        skills: [],
        years_experience: null,
        shift_preference: null,
        personal_email: null,
        mobile_phone: metadata.mobile_phone || metadata.phone_number || null,
        home_phone: null,
        emergency_contact_name: null,
        emergency_contact_phone: null,
        emergency_contact_relationship: null,
        profile_completion_percentage: 0,
        onboarding_step: 'profile_setup',
        onboarding_completed: false,
        onboarding_completed_at: '',
        created_at: authData.user.created_at || new Date().toISOString(),
        updated_at: authData.user.updated_at || new Date().toISOString()
      };
    } catch (error) {
      console.error('Error getting profile from auth metadata:', error);
      return null;
    }
  },

  async createUserProfile(profileData: Partial<UserProfile>): Promise<UserProfile | null> {
    try {
      const { data, error } = await supabase.from('users').insert(profileData).select().single();

      if (error) {
        if (isSystemError(error)) {
          logSystemError(error, 'creating user profile');
        }
        return null;
      }

      return data;
    } catch (error) {
      if (isSystemError(error)) {
        logSystemError(error, 'creating user profile');
      }
      return null;
    }
  },

  async updateUserProfile(
    userId: string,
    updates: Partial<UserProfile>
  ): Promise<UserProfile | null> {
    try {
      // Get tenant_id for the user
      const tenantId = await getTenantIdFromSession(supabase);
      if (!tenantId) {
        console.warn(
          'No tenant_id available for updating user profile, falling back to auth metadata'
        );
        return await this.updateUserProfileInAuth(userId, updates);
      }

      // Prepare updates for prod.user_profiles table
      const dbUpdates: any = {};

      // Handle profile_photo_url - convert to storage path
      if ('profile_photo_url' in updates && updates.profile_photo_url) {
        dbUpdates.profile_photo_path = extractStoragePathFromUrl(updates.profile_photo_url);
      }

      // Handle name fields
      if ('first_name' in updates) {
        dbUpdates.first_name = updates.first_name || null;
      }
      if ('last_name' in updates) {
        dbUpdates.last_name = updates.last_name || null;
      }

      // Handle other fields
      if ('phone_number' in updates) {
        dbUpdates.phone_number = updates.phone_number || null;
      }
      if ('job_title' in updates) {
        dbUpdates.job_title = updates.job_title || null;
      }
      if ('department' in updates) {
        dbUpdates.department = updates.department || null;
      }

      // Use prod schema for user_profiles
      const prodClient = getProdSupabaseClient();

      // Check if profile exists
      const { data: existingProfile } = await prodClient
        .from('user_profiles')
        .select('id')
        .eq('user_id', userId)
        .eq('tenant_id', tenantId)
        .maybeSingle();

      let result;

      if (existingProfile) {
        // Profile exists, update it
        result = await prodClient
          .from('user_profiles')
          .update(dbUpdates)
          .eq('user_id', userId)
          .eq('tenant_id', tenantId)
          .select()
          .single();
      } else {
        // Profile doesn't exist, create it
        result = await prodClient
          .from('user_profiles')
          .insert({
            user_id: userId,
            tenant_id: tenantId,
            ...dbUpdates
          })
          .select()
          .single();
      }

      const { data, error } = result;

      if (error) {
        console.error('Error updating user profile:', error);
        if (isSystemError(error)) {
          logSystemError(error, 'updating user profile');
        }
        // Fallback to auth metadata on error
        return await this.updateUserProfileInAuth(userId, updates);
      }

      if (!data) {
        console.error('No data returned from update - RLS may have blocked update');
        // Fallback to auth metadata
        return await this.updateUserProfileInAuth(userId, updates);
      }

      // Sync name changes to user_approvals if first_name or last_name were updated
      if ('first_name' in updates || 'last_name' in updates) {
        try {
          // Get the updated first_name and last_name from data or updates
          const firstName = data.first_name || updates.first_name || '';
          const lastName = data.last_name || updates.last_name || '';
          const fullName = `${firstName} ${lastName}`.trim() || null;

          if (fullName) {
            // Update user_approvals.full_name for all approvals for this user
            // Use admin client if available, otherwise use regular client (user can update their own)
            const { error: approvalUpdateError } = await prodClient
              .from('user_approvals')
              .update({
                full_name: fullName,
                updated_at: new Date().toISOString()
              })
              .eq('user_id', userId);

            if (approvalUpdateError) {
              console.warn(
                'Failed to sync name to user_approvals (non-critical):',
                approvalUpdateError
              );
              // Non-critical error - don't fail the profile update
            }
          }
        } catch (syncError) {
          console.warn('Error syncing name to user_approvals (non-critical):', syncError);
          // Non-critical error - don't fail the profile update
        }
      }

      // Convert profile_photo_path to full URL
      const profilePhotoUrl = getProfilePhotoUrl(data.profile_photo_path);

      // Map prod.user_profiles data to UserProfile interface
      // Use first_name and last_name from database (data), not auth metadata
      return {
        id: data.id,
        user_id: data.user_id,
        first_name: data.first_name || '',
        last_name: data.last_name || '',
        profile_photo_url: profilePhotoUrl,
        phone_number: data.phone_number || '',
        job_title: data.job_title || '',
        employee_id: '',
        preferred_language: 'en',
        timezone: 'UTC',
        hire_date: null,
        work_phone: null,
        work_email: null,
        department: data.department || '',
        manager_id: null,
        certifications: [],
        specializations: [],
        languages: [],
        skills: [],
        years_experience: null,
        shift_preference: null,
        personal_email: null,
        mobile_phone: data.phone_number || null,
        home_phone: null,
        emergency_contact_name: null,
        emergency_contact_phone: null,
        emergency_contact_relationship: null,
        profile_completion_percentage: 0,
        onboarding_step: 'profile_setup',
        onboarding_completed: false,
        onboarding_completed_at: '',
        created_at: data.created_at,
        updated_at: data.updated_at
      };
    } catch (error) {
      if (isSystemError(error)) {
        logSystemError(error, 'updating user profile');
      }
      // Fallback to auth metadata
      return await this.updateUserProfileInAuth(userId, updates);
    }
  },

  // Helper method to update profile in auth metadata (fallback)
  async updateUserProfileInAuth(
    userId: string,
    updates: Partial<UserProfile>
  ): Promise<UserProfile | null> {
    try {
      const metadataUpdates: any = {};

      if ('profile_photo_url' in updates && updates.profile_photo_url) {
        metadataUpdates.profile_photo_url = updates.profile_photo_url;
      }

      // Handle first_name and last_name - combine into full_name for auth metadata
      if ('first_name' in updates || 'last_name' in updates) {
        const { data: currentAuth } = await supabase.auth.getUser();
        const currentFullName = currentAuth?.user?.user_metadata?.full_name || '';
        const currentParts = currentFullName.split(' ');
        const firstName = updates.first_name ?? currentParts[0] ?? '';
        const lastName = updates.last_name ?? currentParts.slice(1).join(' ') ?? '';
        metadataUpdates.full_name = `${firstName} ${lastName}`.trim() || null;
      }

      if ('phone_number' in updates) {
        metadataUpdates.mobile_phone = updates.phone_number;
        metadataUpdates.phone_number = updates.phone_number;
      }
      if ('job_title' in updates) {
        metadataUpdates.job_title = updates.job_title;
      }
      if ('department' in updates) {
        metadataUpdates.department = updates.department;
      }

      const { data: authData, error: authError } = await supabase.auth.updateUser({
        data: metadataUpdates
      });

      if (authError) {
        console.error('Error updating auth user metadata:', authError);
        return null;
      }

      if (!authData.user) {
        return null;
      }

      // Return profile from updated auth metadata
      return await this.getUserProfileFromAuth(userId);
    } catch (error) {
      console.error('Error updating profile in auth metadata:', error);
      return null;
    }
  },

  // Notification Preferences
  async getNotificationPreferences(userId: string): Promise<NotificationPreferences | null> {
    try {
      const { data, error } = await supabase
        .from('notification_preferences')
        .select('*')
        .eq('user_id', userId)
        .maybeSingle();

      if (error) {
        if (isSystemError(error)) {
          logSystemError(error, 'fetching notification preferences');
        }
        return null;
      }

      if (!data) {
        // Return default preferences if not found
        return {
          id: '',
          user_id: userId,
          email_notifications: true,
          email_recall_alerts: true,
          email_campaign_updates: true,
          email_system_notifications: true,
          email_weekly_reports: false,
          sms_notifications: false,
          sms_urgent_alerts: true,
          sms_phone_number: '',
          in_app_notifications: true,
          in_app_sound: true,
          in_app_badge: true,
          notification_frequency: 'immediate',
          quiet_hours_start: null,
          quiet_hours_end: null,
          quiet_hours_enabled: false,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        };
      }

      return data;
    } catch (error) {
      if (isSystemError(error)) {
        logSystemError(error, 'getting notification preferences');
      }
      return null;
    }
  },

  async updateNotificationPreferences(
    userId: string,
    preferences: Partial<NotificationPreferences>
  ): Promise<NotificationPreferences | null> {
    try {
      const { data, error } = await supabase
        .from('notification_preferences')
        .upsert({ user_id: userId, ...preferences })
        .select()
        .single();

      if (error) {
        if (isSystemError(error)) {
          logSystemError(error, 'updating notification preferences');
        }
        return null;
      }

      return data;
    } catch (error) {
      if (isSystemError(error)) {
        logSystemError(error, 'updating notification preferences');
      }
      return null;
    }
  },

  // API Keys Management
  async getApiKeys(userId: string): Promise<ApiKey[]> {
    try {
      const { data, error } = await supabase
        .from('api_keys')
        .select('*')
        .eq('user_id', userId)
        .eq('is_active', true)
        .order('created_at', { ascending: false });

      if (error) {
        if (isSystemError(error)) {
          logSystemError(error, 'fetching API keys');
        }
        return [];
      }

      return data || [];
    } catch (error) {
      if (isSystemError(error)) {
        logSystemError(error, 'getting API keys');
      }
      return [];
    }
  },

  async createApiKey(
    userId: string,
    keyData: { key_name: string; permissions: string[] }
  ): Promise<ApiKey | null> {
    try {
      // Generate a secure API key
      const keyValue = `vx_${crypto.randomUUID().replace(/-/g, '')}`;
      const keyPrefix = keyValue.substring(0, 8);

      const { data, error } = await supabase
        .from('api_keys')
        .insert({
          user_id: userId,
          key_name: keyData.key_name,
          key_value: keyValue,
          key_prefix: keyPrefix,
          permissions: keyData.permissions,
          is_active: true
        })
        .select()
        .single();

      if (error) {
        if (isSystemError(error)) {
          logSystemError(error, 'creating API key');
        }
        return null;
      }

      return data;
    } catch (error) {
      if (isSystemError(error)) {
        logSystemError(error, 'creating API key');
      }
      return null;
    }
  },

  async revokeApiKey(keyId: string): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('api_keys')
        .update({ is_active: false })
        .eq('id', keyId);

      if (error) {
        if (isSystemError(error)) {
          logSystemError(error, 'revoking API key');
        }
        return false;
      }

      return true;
    } catch (error) {
      if (isSystemError(error)) {
        logSystemError(error, 'revoking API key');
      }
      return false;
    }
  },

  // Onboarding Progress
  async getOnboardingProgress(userId: string): Promise<UserOnboardingProgress | null> {
    try {
      const { data, error } = await supabase
        .from('user_onboarding_progress')
        .select('*')
        .eq('user_id', userId)
        .single();

      if (error) {
        if (isSystemError(error)) {
          logSystemError(error, 'fetching onboarding progress');
        }
        return null;
      }

      return data;
    } catch (error) {
      if (isSystemError(error)) {
        logSystemError(error, 'getting onboarding progress');
      }
      return null;
    }
  },

  async updateOnboardingProgress(
    userId: string,
    updates: Partial<UserOnboardingProgress>
  ): Promise<UserOnboardingProgress | null> {
    try {
      const { data, error } = await supabase
        .from('user_onboarding_progress')
        .upsert({ user_id: userId, ...updates })
        .select()
        .single();

      if (error) {
        if (isSystemError(error)) {
          logSystemError(error, 'updating onboarding progress');
        }
        return null;
      }

      return data;
    } catch (error) {
      if (isSystemError(error)) {
        logSystemError(error, 'updating onboarding progress');
      }
      return null;
    }
  },

  async completeOnboardingStep(userId: string, step: string): Promise<boolean> {
    try {
      const progress = await this.getOnboardingProgress(userId);
      if (!progress) {
        // Create initial progress record
        await this.updateOnboardingProgress(userId, {
          current_step: step,
          completed_steps: [step],
          approval_status: 'pending'
        });
      } else {
        // Update existing progress
        const completedSteps = [...(progress.completed_steps || []), step];
        await this.updateOnboardingProgress(userId, {
          current_step: step,
          completed_steps: completedSteps
        });
      }

      return true;
    } catch (error) {
      if (isSystemError(error)) {
        logSystemError(error, 'completing onboarding step');
      }
      return false;
    }
  },

  // User Preferences
  async getUserPreferences(userId: string): Promise<UserPreferences | null> {
    try {
      const { data, error } = await supabase
        .from('user_preferences')
        .select('*')
        .eq('user_id', userId)
        .single();

      if (error) {
        if (isSystemError(error)) {
          logSystemError(error, 'fetching user preferences');
        }
        return null;
      }

      return data;
    } catch (error) {
      if (isSystemError(error)) {
        logSystemError(error, 'getting user preferences');
      }
      return null;
    }
  },

  async updateUserPreferences(
    userId: string,
    preferences: Partial<UserPreferences>
  ): Promise<UserPreferences | null> {
    try {
      const { data, error } = await supabase
        .from('user_preferences')
        .upsert({ user_id: userId, ...preferences })
        .select()
        .single();

      if (error) {
        if (isSystemError(error)) {
          logSystemError(error, 'updating user preferences');
        }
        return null;
      }

      return data;
    } catch (error) {
      if (isSystemError(error)) {
        logSystemError(error, 'updating user preferences');
      }
      return null;
    }
  },

  // Profile Completion Calculation
  async calculateProfileCompletion(userId: string): Promise<number> {
    try {
      const profile = await this.getUserProfile(userId);
      if (!profile) return 0;

      const requiredFields = [
        'phone_number',
        'job_title',
        'employee_id',
        'department',
        'hire_date',
        'work_phone'
      ];

      const optionalFields = [
        'personal_email',
        'mobile_phone',
        'emergency_contact_name',
        'emergency_contact_phone',
        'certifications',
        'specializations',
        'skills'
      ];

      let completedRequired = 0;
      let completedOptional = 0;

      requiredFields.forEach(field => {
        if (profile[field as keyof UserProfile] && profile[field as keyof UserProfile] !== '') {
          completedRequired++;
        }
      });

      optionalFields.forEach(field => {
        if (profile[field as keyof UserProfile] && profile[field as keyof UserProfile] !== '') {
          completedOptional++;
        }
      });

      // Calculate percentage: 70% for required fields, 30% for optional fields
      const requiredPercentage = (completedRequired / requiredFields.length) * 70;
      const optionalPercentage = (completedOptional / optionalFields.length) * 30;

      return Math.round(requiredPercentage + optionalPercentage);
    } catch (error) {
      if (isSystemError(error)) {
        logSystemError(error, 'calculating profile completion');
      }
      return 0;
    }
  }
};
